import { Theme, Tooltip, Typography } from '@mui/material';
import { important } from 'common-types';
import { FC } from 'react';
import { MdAccessTimeFilled as AccessTimeFilledIcon } from 'react-icons/md';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';
import { useIsScanning } from '../../new-scan/actions/scan-actions';
import ChartPanelLayout from '../components/ChartPanelLayout';
import DebugInfoDialog from '../components/DebugInfoDialog';
import IssuesGraph from '../components/IssuesGraph';
import SkeletonLoader from '../components/SkeletonLoader';
import ExecutiveStore from '../stores/executive-store';
import SeverityBoxContainer from './SeverityBoxContainer';

const ExecutiveSummaryGraphContainer: FC = () => {
  const { classes } = useStyles();
  const isScanning = useIsScanning();
  const {
    issuesScore,
    lastScanDate,
    scanFinishDateFormatted,
    scanStartDateFormatted,
    scanAdditionalInfo,
    scanID,
  } = useSnapshot(ExecutiveStore);

  const lastScanMarkup = (
    <div className={classes.lastScanContainer}>
      <AccessTimeFilledIcon />
      <Typography
        sx={{ cursor: 'default', userSelect: 'none' }}
        variant='body2'
        color='text.secondary'
        component='span'>
        Last Scan:
      </Typography>
      <Typography
        variant='body2'
        color='text.primary'
        component='span'
        className={classes.lastScanDate}>
        {lastScanDate?.relativeDate}
      </Typography>
    </div>
  );

  return (
    <ChartPanelLayout
      header={
        <div className={classes.header}>
          <Typography color='text.secondary' variant='body2' fontWeight={600}>
            Issues Trend
          </Typography>
          <DebugInfoDialog
            scanAdditionalInfo={scanAdditionalInfo}
            scanFinishDate={scanFinishDateFormatted}
            scanStartDate={scanStartDateFormatted}
            lastScanDate={lastScanDate}
            scanId={scanID}>
            <Tooltip
              arrow
              placement='bottom'
              title={
                <>
                  {lastScanDate?.lastFormatScanDate} at{' '}
                  {lastScanDate?.lastFormatScanTime}
                </>
              }>
              {lastScanMarkup}
            </Tooltip>
          </DebugInfoDialog>
        </div>
      }>
      <SkeletonLoader
        message={isScanning ? 'Waiting for issues' : 'Loading'}
        loading={!issuesScore || issuesScore.labels.length === 0}>
        <div className={classes.container}>
          <div className={classes.severitiesContainer}>
            <SeverityBoxContainer />
          </div>
          <div className={classes.chartContainer}>
            {issuesScore && <IssuesGraph {...issuesScore} />}
          </div>
        </div>
      </SkeletonLoader>
    </ChartPanelLayout>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  executiveSummaryGraphContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  container: {
    flexGrow: 1,
    display: 'flex',
    height: '100%',
  },
  severitiesContainer: {
    flexShrink: 0,
  },
  chartContainer: {
    flex: '1 1 auto',
    overflow: 'hidden',
    wordBreak: 'break-all',
  },
  lastScanContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& svg': {
      color: theme.palette.text.secondary,
    },
    '& > *': {
      lineHeight: important('1'),
    },
  },
  lastScanDate: {
    textTransform: 'capitalize',
  },
}));

export default ExecutiveSummaryGraphContainer;
